import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import useGetUserAccessories from '@/Components/Hooks/useGetUserAccessories';

export const useAssignSmartCameraHandler = (
  fetchDevices,
  hubRecord,
  setDeviceStatus,
  setFilteredAccessoriesData,
  setSelectedDevice
) => {
  const [isSmartCameraModalOpen, setIsSmartCameraModalOpen] = useState(false);
  const [readyToAssignDevices, setReadyToAssignDevices] = useState([]);
  const [searchText, setSearchText] = useState(null);
  const [searchApplied, setSearchApplied] = useState(false);
  const [selectedSmartCameraDevice, setSelectedSmartCameraDevice] = useState(null);
  const [readyToAssignloading, setReadyToAssignLoading] = useState(false);
  const userApi = useSelector((state) => state.apis.userApi);

  const {
    data: accessoriesData = [],
    isFetched: dataFetched,
    refetch,
  } = useGetUserAccessories(hubRecord);

  const handleDeviceSmartCameraSelect = (device) => {
    setSelectedSmartCameraDevice(device); // Set the selected device
  };

  const handleSearchApply = () => {
    if (!searchText) {
      setSearchApplied(false);
      fetchDevices(null, (fetchedDevices) => {
        const readyToAssign = fetchedDevices.filter(
          (device) => device.provisioningstatus === 'Provisioned' && !device.accountname
        );
        setReadyToAssignDevices(readyToAssign); // Reset to original device list
      });
      return;
    }
    const lowerCaseQuery = searchText.toLowerCase();
    fetchDevices(null, (fetchedDevices) => {
      const readyToAssign = fetchedDevices.filter(
        (device) => device.provisioningstatus === 'Provisioned' && !device.accountname
      );
      const filteredDevices = readyToAssign.filter((device) =>
        device.deviceid?.toLowerCase().includes(lowerCaseQuery)
      );
      setReadyToAssignDevices(filteredDevices);
      setSearchApplied(true);
    });
  };

  const handleClearSearch = () => {
    setSearchText(null);
    setSearchApplied(false);
    // Re-fetch the devices and reset the readyToAssignDevices list
    const queryParams = { filterkey: 'useruuid', filtervalue: hubRecord?.useruuid };
    fetchDevices(queryParams, (fetchedDevices) => {
      const readyToAssign = fetchedDevices?.filter(
        (device) => device.provisioningstatus === 'Provisioned' && !device.accountname
      );
      setReadyToAssignDevices(readyToAssign); // Reset the list with the original devices
      if (setDeviceStatus) {
        if (fetchedDevices.length > 0) {
          setDeviceStatus(fetchedDevices[0].deviceconnectstatus?.toLowerCase());
          setSelectedDevice((prev) => {
            // for updating popup modal
            return {
              ...prev,
              deviceuuid: fetchedDevices[0].deviceuuid,
              lastconnecttime: fetchedDevices[0].lastconnecttime_utc,
              lastdisconnecttime: fetchedDevices[0].lastdisconnecttime_utc,
            };
          });
          setFilteredAccessoriesData((prev) => {
            // for updating list...
            const matchedItem = prev.findIndex(
              (item) => item.deviceuuid === fetchedDevices[0].deviceuuid
            );
            if (matchedItem > -1) {
              const newData = [...prev];
              newData[matchedItem] = {
                ...newData[matchedItem],
                lastconnecttime: fetchedDevices[0].lastconnecttime_utc,
                lastdisconnecttime: fetchedDevices[0].lastdisconnecttime_utc,
              };
              return [...newData];
            }
            return prev;
          });
        }
      }
    });
  };

  // Function to handle the modal opening
  const handleAssignSmartCamera = () => {
    setIsSmartCameraModalOpen(true);
    handleAssignSmartCameraList();
  };

  // Function to close the modal
  const closeModal = () => {
    setIsSmartCameraModalOpen(false);
    setSearchText(null); // Clears search input
    setSearchApplied(false); // Resets search state
    setSelectedSmartCameraDevice(null);
  };
  const handleAssignSmartCameraList = () => {
    setReadyToAssignLoading(true);
    // Call fetchDevices and handle the data
    fetchDevices(null, (fetchedDevices) => {
      setReadyToAssignLoading(false);
      if (!fetchedDevices || fetchedDevices.length === 0) {
        return;
      }
      // Filter devices ready for assignment do not include those devices which are set for TO_DO_FACTORYRESET
      const readyToAssign = fetchedDevices.filter(
        (device) =>
          device.provisioningstatus === 'Provisioned' &&
          !device.accountname &&
          device.operationalstatus != 'TO_DO_FACTORYRESET'
      );
      if (readyToAssign.length === 0) {
        console.warn('No devices are ready for assignment');
      } else {
        setReadyToAssignDevices(readyToAssign); // Store the filtered devices
      }
    });
  };

  const assignDevice = (
    hubRecord,
    device,
    trans,
    fetchDevices,
    setHasDevice,
    closeModal,
    refetch,
    queryParams,
    setDeviceLocation,
    setDeviceStatus,
    setStatusTitle,
    setHeaderRefreshKey
  ) => {
    const errorCallback = (error) => {
      console.error('Error assigning device:', error);
      // message.error("Cannot Assign Device to User: " + error.message);
    };
    const successCallback = () => {
      // message.success("Device assigned successfully");
      setHasDevice(true);
      closeModal();
      // Fetch updated list of devices after successful assignment
      fetchDevices(queryParams, (fetchedDevices) => {
        if (fetchedDevices?.length > 0) {
          const assignedDevice = fetchedDevices.find((d) => d.deviceuuid === device.deviceuuid);
          if (assignedDevice) {
            setDeviceStatus(assignedDevice.deviceconnectstatus.toLowerCase());
            setDeviceLocation(assignedDevice.location);
            setStatusTitle('Smart Camera');
            setHeaderRefreshKey('assign_device', assignedDevice.deviceid);
          }
        }
      });
      refetch();
    };
    try {
      const userAssociation = {
        deviceuuid: device.deviceuuid,
      };
      userApi.associateDeviceToUser(
        hubRecord.useruuid,
        userAssociation,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  return {
    isSmartCameraModalOpen,
    setIsSmartCameraModalOpen,
    readyToAssignDevices,
    searchText,
    setSearchText,
    searchApplied,
    setSearchApplied,
    selectedSmartCameraDevice,
    setSelectedSmartCameraDevice,
    readyToAssignloading,
    handleDeviceSmartCameraSelect,
    handleSearchApply,
    handleClearSearch,
    handleAssignSmartCamera,
    closeModal,
    handleAssignSmartCameraList,
    assignDevice,
    accessoriesData,
    dataFetched,
    refetch,
  };
};
